import { PICK_FRAMES_PER_OP, PICK_FRAMES_PER_TILESET, PICK_FRAMES_PER_WORLD, grouped } from "../assets/budget.ts";
import type { Command } from "../document/commands.ts";
import { MAX_TILE_ID, assertTilesetListFits } from "../document/packed-tile.ts";
import { describeTilesetUse, framePlacements, tilesetUses, type TilesetUse } from "../document/tileset-change.ts";
import { framesAmong, framesWithout, holdsFrame, sortedFrames, tilesetEntry } from "../document/tileset-frames.ts";
import { dropStep, entriesById, firstUses, pickOverflow, pullStep, stepsCommand, wholeStep, type PickStepOf } from "../document/tileset-picks.ts";
import type { TileRef, TilesetRef, WorldDocument } from "../document/types.ts";

/**
 * A world's packs (ADR-037 and its P1.6-T8 amendment). A pack is the unit Daedalus's importer reads, and it
 * prefixes every tileset id it yields (`<pack>:<tileset>`); a world uses a pack while its document lists a
 * tileset with the pack's prefix. An entry may be partial, naming the frames pulled one at a time from a pack
 * the world does not otherwise use (ADR-129 decision 3): the whole entries are the pack list, and a partial
 * entry offers its frames as props and nothing else. Which packs exist, their names and what they cost is the
 * host's table, handed to the editor as `PackOption`s; nothing here names one.
 */

/**
 * The separator between a pack and a tileset name in a tileset id, as the importer writes it.
 */
const SEPARATOR = ":";

/**
 * A pack as the host's table offers it: the prefix its tileset ids carry, a display name, the tileset ids
 * its import yields in order, and the pages of the editor's atlas it fills on its own.
 */
export interface PackOption {
  readonly id: string;
  readonly name: string;
  readonly tilesets: readonly string[];
  readonly pages: number;
}

/**
 * The pack a tileset id belongs to.
 *
 * @param tilesetId Tileset id.
 * @returns The prefix before the first separator, or null when the id carries none.
 */
export function packOf(tilesetId: string): string | null {
  const separator = tilesetId.indexOf(SEPARATOR);
  return separator <= 0 ? null : tilesetId.slice(0, separator);
}

/**
 * The distinct packs of a list of tileset ids, in first-seen order, which is the order a compile imports
 * them in.
 *
 * @param tilesetIds Tileset ids.
 * @returns Pack ids; an id without a prefix names none.
 */
export function packsOf(tilesetIds: readonly string[]): string[] {
  const packs: string[] = [];
  for (const id of tilesetIds) {
    const pack = packOf(id);
    if (pack !== null && !packs.includes(pack)) {
      packs.push(pack);
    }
  }
  return packs;
}

/**
 * The distinct packs a tileset list holds a whole entry of, in first-seen order: the packs the world uses as
 * packs. A pack with only partial entries is not one of them, so it can still be added whole.
 *
 * @param tilesets The world's tileset list.
 * @returns Pack ids; an id without a prefix names none.
 */
export function wholePacksOf(tilesets: readonly TilesetRef[]): string[] {
  return packsOf(tilesets.filter((tileset) => tileset.frames === undefined).map((tileset) => tileset.id));
}

/**
 * How many of a pack's tilesets a list holds whole: the measure the Packs dialog shows as "4 of 12 tilesets". A
 * partial entry is not counted, a tileset listed twice counts once, and a whole entry the pack's table does not
 * name (a tileset a newer pack version dropped) is not counted.
 *
 * @param tilesets The world's tileset list.
 * @param packTilesets The pack's tileset ids, as the host's table gives them.
 * @returns The number of the pack's tilesets the list holds whole.
 */
export function listedOfPack(tilesets: readonly TilesetRef[], packTilesets: readonly string[]): number {
  const whole = new Set(wholeTilesetIds(tilesets));
  return new Set(packTilesets.filter((id) => whole.has(id))).size;
}

/**
 * The ids of the tilesets a list holds whole, in order: the tilesets whose tiles and terrains the editor offers.
 * A tileset the world only pulled frames from offers props and nothing else (ADR-129 decision 3.4).
 *
 * @param tilesets The world's tileset list.
 * @returns The ids of its whole entries.
 */
export function wholeTilesetIds(tilesets: readonly TilesetRef[]): string[] {
  return tilesets.filter((tileset) => tileset.frames === undefined).map((tileset) => tileset.id);
}

/**
 * The ids of the tilesets an editor offers tiles and terrains of, in the list's order: the whole entries its
 * loaded catalogue lists. A pack made whole after a pull is listed whole at once, but its art arrives with the
 * next compile, and until then the catalogue lists of it only the tilesets props were pulled from. A world
 * loaded with no catalogue offers every whole entry.
 *
 * @param tilesets The world's tileset list.
 * @param loaded The tilesets of the catalogue the editor loaded.
 * @returns The ids.
 */
export function offeredTilesetIds(tilesets: readonly TilesetRef[], loaded: readonly { readonly id: string }[]): string[] {
  const whole = wholeTilesetIds(tilesets);
  if (loaded.length === 0) {
    return whole;
  }
  const held = new Set(loaded.map((tileset) => tileset.id));
  return whole.filter((id) => held.has(id));
}

/**
 * Whether a tileset list offers a frame: its tileset has a whole entry, or a partial entry that names the
 * tile. A strip is asked about by its first frame, which is what a pull stores (ADR-129 decision 3.2). This is
 * what the editor offers, never a rule on what a map may place.
 *
 * @param tilesets The world's tileset list.
 * @param ref The frame.
 * @returns True when the list offers it.
 */
export function offersFrame(tilesets: readonly TilesetRef[], ref: TileRef): boolean {
  const entry = tilesets.find((tileset) => tileset.id === ref.tileset);
  return entry !== undefined && (entry.frames === undefined || holdsFrame(entry.frames, ref.tile));
}

/**
 * Whether a tileset list offers a frame through a pick: the tileset has a partial entry that names the tile. A whole
 * entry does not count, since a tab no longer holds the frames of a tileset it lists whole until the library has
 * brought them (ADR-130 decision 2.6).
 *
 * @param tilesets The world's tileset list.
 * @param ref The frame.
 * @returns True when a partial entry names the tile.
 */
export function offersPick(tilesets: readonly TilesetRef[], ref: TileRef): boolean {
  const entry = tilesets.find((tileset) => tileset.id === ref.tileset);
  return entry !== undefined && entry.frames !== undefined && holdsFrame(entry.frames, ref.tile);
}

/**
 * The first placement of each pack the world places anything from, in document order: what a removal of
 * that pack is refused with.
 *
 * @param world The document.
 * @returns Placement by pack id, in the order the packs are first placed.
 */
export function packPlacements(world: WorldDocument): Map<string, TilesetUse> {
  const placements = new Map<string, TilesetUse>();
  for (const use of tilesetUses(world)) {
    const pack = packOf(use.tileset);
    if (pack !== null && !placements.has(pack)) {
      placements.set(pack, use);
    }
  }
  return placements;
}

/**
 * The command that adds a pack to a world: its tilesets the world does not list yet, appended after every
 * existing entry, and each tileset of it the world holds a partial entry of made whole in place, at the same
 * index (ADR-129 decision 3.4). No packed tile index moves.
 *
 * @param world The document.
 * @param packId The pack.
 * @param tilesets The pack's tileset ids, in the order its import yields them.
 * @returns The command, or null when the world already lists every one whole.
 * @throws Error when the list is empty or holds a tileset of another pack.
 */
export function addPackCommand(world: WorldDocument, packId: string, tilesets: readonly string[]): Command | null {
  if (tilesets.length === 0) {
    throw new Error(`pack ${packId} has no tilesets to add`);
  }
  const foreign = tilesets.find((id) => packOf(id) !== packId);
  if (foreign !== undefined) {
    throw new Error(`tileset ${foreign} is not in pack ${packId}`);
  }
  const held = entriesById(world.tilesets);
  return stepsCommand(world, [...new Set(tilesets)].map((id): PickStepOf => [id, wholeStep(held(id))]));
}

/**
 * The command that removes a pack from a world: every tileset with its prefix, whole or partial, wherever it
 * stands.
 *
 * @param world The document.
 * @param packId The pack.
 * @returns The command, or null when the world lists none of the pack's tilesets.
 * @throws Error when the world still places anything from the pack, naming the first placement.
 */
export function removePackCommand(world: WorldDocument, packId: string): Command | null {
  const placed = packPlacements(world).get(packId);
  if (placed !== undefined) {
    throw new Error(`pack ${packId} is still placed: ${describeTilesetUse(placed, world.maps.length > 1)}`);
  }
  const remove = world.tilesets.flatMap((t, index) => (packOf(t.id) === packId ? [{ index, ...tilesetEntry(t.id, t.frames) }] : []));
  return remove.length === 0 ? null : { kind: "changeTilesets", remove, insert: [] };
}

/**
 * The command that pulls frames of one tileset into a world (ADR-129 decision 3.4): a partial entry of them
 * at the end of the list when the tileset is not listed, or the entry the world holds widened by the frames it
 * lacks. For a strip the frame pulled is its first. The pick caps bind here, since this is where a pick is
 * made.
 *
 * @example
 * const command = pullFramesCommand(world, "highlands:Atlas-Props-Sprites", [3]);
 *
 * @param world The document.
 * @param tileset The tileset.
 * @param frames The local tile ids to pull, in any order.
 * @returns The command, or null when the world lists the tileset whole or already holds every frame.
 * @throws Error when no frame is named, one is not a whole number from 0 to `MAX_TILE_ID`, the pull adds more
 *   than `PICK_FRAMES_PER_OP` frames, the entry would hold more than `PICK_FRAMES_PER_TILESET`, the world's
 *   partial entries more than `PICK_FRAMES_PER_WORLD` together, or the list more entries than the packed tile
 *   form indexes.
 */
export function pullFramesCommand(world: WorldDocument, tileset: string, frames: readonly number[]): Command | null {
  return pullPicksCommand(world, [{ tileset, frames }]);
}

/**
 * The command that pulls frames of several tilesets as one (ADR-129 decision 3.9): what a prop brings with it
 * lands whole in one command and one undo, or the pull is refused whole. Each pick is what
 * `pullFramesCommand` does for its tileset; the frames added by all of them together count against
 * `PICK_FRAMES_PER_OP`, since they travel as one operation, and the caps bind on the list they leave.
 *
 * @param world The document.
 * @param picks The tilesets and the local tile ids to pull from each, in any order; a tileset named twice is
 *   named once with both lists.
 * @returns The command, or null when the world lists every tileset whole or already holds every frame.
 * @throws Error when no pick or no frame is named, a frame is not a whole number from 0 to `MAX_TILE_ID`, the
 *   pull adds more than `PICK_FRAMES_PER_OP` frames in all, an entry would hold more than
 *   `PICK_FRAMES_PER_TILESET`, the world's partial entries more than `PICK_FRAMES_PER_WORLD` together, or the
 *   list more entries than the packed tile form indexes.
 */
export function pullPicksCommand(world: WorldDocument, picks: readonly { readonly tileset: string; readonly frames: readonly number[] }[]): Command | null {
  const named = new Map<string, number[]>();
  for (const pick of picks) {
    if (pick.frames.length === 0) {
      throw new Error("a pull names at least one frame");
    }
    if (pick.frames.some((frame) => !Number.isInteger(frame) || frame < 0 || frame > MAX_TILE_ID)) {
      throw new Error(`a pulled frame is a whole number from 0 to ${MAX_TILE_ID}`);
    }
    named.set(pick.tileset, [...(named.get(pick.tileset) ?? []), ...pick.frames]);
  }
  if (named.size === 0) {
    throw new Error("a pull names at least one frame");
  }
  const held = entriesById(world.tilesets);
  const steps: PickStepOf[] = [];
  let added = 0;
  for (const [tileset, frames] of named) {
    const tiles = sortedFrames(frames);
    const step = pullStep(held(tileset), tiles);
    if (step.kind === "insert" || step.kind === "frames") {
      added += step.kind === "insert" ? tiles.length : framesWithout(step.after ?? [], step.before ?? []).length;
      steps.push([tileset, step]);
    }
  }
  if (steps.length === 0) {
    return null;
  }
  if (added > PICK_FRAMES_PER_OP) {
    throw new Error(`a pull adds at most ${grouped(PICK_FRAMES_PER_OP)} frames at once; this one adds ${grouped(added)}`);
  }
  const over = pickOverflow(world.tilesets, steps, new Set(steps.map(([id]) => id)));
  if (over?.cap === "tileset") {
    throw new Error(`${over.id} would hold ${grouped(over.frames)} pulled frames; an entry holds at most ${grouped(PICK_FRAMES_PER_TILESET)}. Add the whole pack to use more of it`);
  }
  if (over?.cap === "world") {
    throw new Error(`the world would hold ${grouped(over.frames)} pulled frames; it holds at most ${grouped(PICK_FRAMES_PER_WORLD)}. Add a whole pack to use more of it`);
  }
  if (over?.cap === "list") {
    assertTilesetListFits(over.entries, "the tileset list would hold");
  }
  return stepsCommand(world, steps);
}

/**
 * The command that takes pulled frames out of a world again (ADR-129 decision 3.4): the entry narrowed by the
 * frames named that it holds, or removed when its last frame goes. A frame is placed while a prop's frame, a
 * state frame or a transition animation names its tileset and tile, and a placed frame is never dropped. The
 * removal of the entry is the document's own, refused while anything at all names the tileset: a tile cell, a
 * terrain layer or a prop on a frame nobody pulled.
 *
 * @param world The document.
 * @param tileset The tileset.
 * @param frames The local tile ids to drop, in any order; one the entry does not hold is passed over.
 * @returns The command, or null for a whole entry, an unlisted tileset or frames the entry does not hold.
 * @throws Error naming the first placement when a named frame is still placed, or, when the last frame would
 *   go, naming the first thing that still names the tileset. Nothing changes then.
 */
export function dropFramesCommand(world: WorldDocument, tileset: string, frames: readonly number[]): Command | null {
  const held = world.tilesets.find((entry) => entry.id === tileset);
  if (held?.frames === undefined) {
    return null;
  }
  const asked = new Set(framesAmong(held.frames, frames));
  const withMap = world.maps.length > 1;
  const placed = framePlacements(world, tileset);
  for (const [tile, use] of placed) {
    if (asked.has(tile)) {
      throw new Error(`tileset ${tileset} is still placed: ${describeTilesetUse(use, withMap)}`);
    }
  }
  const step = dropStep(held, [...asked], () => placed, firstUses(world));
  if (step.kind === "held") {
    throw new Error(`tileset ${tileset} is still placed: ${describeTilesetUse(step.use, withMap)}`);
  }
  return stepsCommand(world, [[tileset, step]]);
}
